import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Typography, TextField } from '@material-ui/core';
import { get, isEmpty } from 'lodash';
import Autocomplete from '@material-ui/lab/Autocomplete';

import { Box, Button, FileUpload } from 'components';
import {
  uploadFileSignedUrl,
  addInvestorDocumentByName,
  getInvestmentDocuments,
} from 'services';
import { errorMessageHandler, formatAmount } from 'common/utils/helpers';
import { RootState } from 'redux-modules/store/rootState';
import { FN } from 'common/types';

import styles from './styles';

type Props = {
  spv: string;
  upcomingDistribution: Record<string, any>;
  onConfirmPartialReInvest: FN;
  setReceiveDistributionModal: any;
  refereshUpcomingDistribution: any;
};

const ReceiveEntireDistribution = ({
  spv,
  upcomingDistribution,
  onConfirmPartialReInvest,
  setReceiveDistributionModal,
  refereshUpcomingDistribution,
}: Props) => {
  const classes = styles();
  const fileUploadRef = useRef<any>();
  const [loadingSignedUrl, setLoadingSignedUrl] = React.useState<boolean>(
    false,
  );
  const [fileuploadReq, setFileuploadReq] = React.useState<any>('');
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [errorMessage, setErrorMessage] = React.useState<string>('');
  const [documents, setDocuments] = React.useState<any>([]);
  const [selectedPercentage, setSelectedPercentage] = React.useState<string>(
    '',
  );
  const [errorPercentage, setErrorPercentage] = React.useState<string>('');

  const { investor } = useSelector(({ Investor }: RootState) => Investor);

  const { investmentDetail } = investor;
  const investmentData = investmentDetail?.find(
    (investment: any) => investment.spv === spv,
  );

  const handleSelectPercentage = (value: string) => {
    value = value && value.replace('%', '');
    setSelectedPercentage('');
    if (!isEmpty(value)) {
      if (value.indexOf('.') !== -1) {
        setErrorPercentage('decimal not allowed');
      } else {
        if (
          isNaN(parseInt(value)) ||
          !Number.isInteger(parseInt(value)) ||
          parseInt(value) > 100 ||
          parseInt(value) <= 0
        ) {
          setErrorPercentage('Invalid value');
        } else {
          setErrorPercentage('');
          setSelectedPercentage(value);
        }
      }
    }
  };

  const getRemainingAmount = () => {
    const balance = parseFloat(get(upcomingDistribution, 'balance'));
    return ((100 - parseInt(selectedPercentage)) / 100) * balance;
  };

  const onSubmit = async (): Promise<void> => {
    setErrorMessage('');
    if (!isEmpty(fileuploadReq)) {
      setIsLoading(true);
      try {
        // eslint-disable-next-line no-undef
        await fetch(get(fileuploadReq, 'url.uploadUrl'), {
          method: 'PUT',
          body: fileuploadReq.file,
        });
        const addDocumentPayload: any = {
          filename: fileuploadReq.filename,
          documentName: 'RCVD_DIST_BANK_DETAILS',
          documentUrl: get(fileuploadReq, 'url.accessUrl'),
          investmentDetailId: get(investmentData, 'id'),
          receivedFullAmount:
            parseInt(selectedPercentage) !== 100 ? false : true,
          amountToBeReceived:
            parseFloat(get(upcomingDistribution, 'balance')) -
            getRemainingAmount(),
        };
        const existingDocList: any = uploadedFiles();
        if (!isEmpty(existingDocList))
          addDocumentPayload.id = existingDocList[0].id;
        await addInvestorDocumentByName(addDocumentPayload);
        getInvestmentAllDocument();
        setFileuploadReq('');
        fileUploadRef?.current?.afterUpload();
        if (parseInt(selectedPercentage) !== 100) {
          onConfirmPartialReInvest();
        } else {
          setReceiveDistributionModal(false);
          refereshUpcomingDistribution();
        }
      } catch (err) {
        const message: string = errorMessageHandler(err);
        setErrorMessage(message);
      } finally {
        setIsLoading(false);
      }
    } else {
      setErrorMessage('Please upload bank details.');
    }
  };

  const uploadedFiles = () => {
    if (isEmpty(documents)) return [];
    if (!isEmpty(documents)) {
      return documents.filter(
        (doc: any) => doc.documentName === 'RCVD_DIST_BANK_DETAILS',
      );
    }
  };

  const getInvestmentAllDocument = () => {
    if (!isEmpty(investmentData)) {
      setErrorMessage('');
      setIsLoading(true);
      getInvestmentDocuments({
        id: get(investmentData, 'id'),
        type: 'NON_DOCU_SIGN_DOCUMENTS',
      })
        .then((res: any) => {
          setDocuments(res);
          setIsLoading(false);
        })
        .catch((err: any) => {
          const message: string = errorMessageHandler(err);
          setErrorMessage(message);
          setIsLoading(false);
        });
    }
  };

  useEffect(() => {
    getInvestmentAllDocument();
  }, []);

  return (
    <Box className={classes.entireDistribution}>
      <Box className={classes.inputWrap}>
        <Typography className={classes.distributionText}>
          There is a distributable amount of{' '}
          <span className={classes.amount}>
            {formatAmount(get(upcomingDistribution, 'balance'))}
          </span>
          .{' '}
          <span>
            Please enter the % of distributable amount you wish to redeem in
            cash:
            <span>
              <Autocomplete
                id="field1"
                freeSolo
                disableClearable
                className={classes.selectInput}
                options={['100', '75', '50', '25']}
                value={selectedPercentage}
                onInputChange={(_: any, value: string) => {
                  handleSelectPercentage(value);
                }}
                renderInput={(params: any) => (
                  <>
                    <Box className={classes.amountTextField}>
                      <TextField
                        {...params}
                        type="number"
                        margin="normal"
                        variant="standard"
                        error={!!errorPercentage}
                        className={classes.selectValInput}
                        // InputProps={{
                        //   classes: { root: classes.inputRoot },
                        //   // autoComplete: 'new-password', // disable autocomplete and autofill
                        //   endAdornment: '%',
                        // }}
                        InputLabelProps={{
                          classes: {
                            root: classes.labelRoot,
                            focused: classes.labelFocused,
                          },
                        }}
                      />
                      <span className={classes.percentage}>%</span>
                    </Box>
                  </>
                )}
              />
            </span>
          </span>
        </Typography>
      </Box>
      {selectedPercentage &&
        !errorPercentage &&
        parseInt(selectedPercentage) !== 100 && (
          <Box>
            <Typography className={classes.distributionText}>
              Remaining amount of {100 - parseInt(selectedPercentage)}%{' '}
              <span className={classes.amount}>
                ({formatAmount(getRemainingAmount())})
              </span>{' '}
              will be rolled over.
            </Typography>
          </Box>
        )}
      <Typography className={classes.distributionText}>
        Please upload your bank account details to receive the cash distribution
      </Typography>
      <FileUpload
        getSignedUrl={uploadFileSignedUrl}
        uploadedFiles={[]}
        setLoadingSignedUrl={setLoadingSignedUrl}
        onSuccess={(req: any) => setFileuploadReq(req)}
        requestObject={{
          documentName: 'RCVD_DIST_BANK_DETAILS',
          investmentDetailId: get(investmentData, 'id'),
        }}
        content={
          <Button
            name="Upload Your Bank Account Details"
            className={classes.uploadBtn}
            isLoading={loadingSignedUrl || isLoading}
          />
        }
        ref={fileUploadRef}
      />
      {errorMessage && (
        <Typography
          variant="body1"
          gutterBottom
          className={classes.errorMessage}
        >
          {errorMessage}
        </Typography>
      )}
      <Box className={classes.nextPrevButtonBox}>
        {/* <Button
          variant="outlined"
          className={classes.prevButton}
          name="Back"
          disabled={loadingSignedUrl || isLoading}
        /> */}
        <Button
          className={classes.nextButton}
          style={{ width: parseInt(selectedPercentage) === 100 ? 152 : 220 }}
          name={
            parseInt(selectedPercentage) === 100
              ? 'Submit'
              : 'Submit and Continue'
          }
          disabled={loadingSignedUrl || isLoading}
          onClick={() => onSubmit()}
          // onClick={() => history.push('/dashboard')}
        />
      </Box>
    </Box>
  );
};
export default ReceiveEntireDistribution;
